/**
 * Page Panier (US-017)
 * E-Store - FrontOffice
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, useCart } from '@e-store/shared';
import { AmazonHeader } from '../components/AmazonHeader';
import './CartPage.css';

export function CartPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { cart, updateCartItem, removeFromCart, clearCart, isLoading } = useCart();

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const handleUpdateQuantity = async (articleId: string, newQuantity: number) => {
    if (newQuantity < 1) return;

    const result = await updateCartItem({ articleId, quantity: newQuantity });
    if (!result.success) {
      setError(result.error || 'Erreur lors de la mise a jour');
    }
  };

  const handleRemove = async (articleId: string) => {
    const result = await removeFromCart(articleId);
    if (result.success) {
      setSuccess('Article retire du panier');
      setTimeout(() => setSuccess(''), 2000);
    } else {
      setError(result.error || 'Erreur lors de la suppression');
    }
  };

  const handleClearCart = async () => {
    const result = await clearCart();
    if (result.success) {
      setSuccess('Panier vide');
      setTimeout(() => setSuccess(''), 2000);
    } else {
      setError(result.error || 'Erreur lors du vidage du panier');
    }
    setShowClearConfirm(false);
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login?redirect=/checkout');
      return;
    }
    navigate('/checkout');
  };

  const formatPrice = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const isEmpty = !cart || cart.items.length === 0;

  return (
    <div className="cart-page">
      {/* Header Amazon */}
      <AmazonHeader showSearch={true} />

      {/* Page Content */}
      <div className="page-container">
        <div className="page-header">
          <h1 className="page-title">Mon Panier</h1>
          <p className="page-description">
            {isEmpty ? 'Votre panier est vide' : `${cart.itemCount} article${cart.itemCount !== 1 ? 's' : ''} dans votre panier`}
          </p>
        </div>

        {/* Messages */}
        {error && (
          <div className="message error">
            {error}
            <button onClick={() => setError('')}>x</button>
          </div>
        )}
        {success && (
          <div className="message success">
            {success}
            <button onClick={() => setSuccess('')}>x</button>
          </div>
        )}

        {isEmpty ? (
          <div className="empty-cart">
            <div className="empty-cart-icon">🛒</div>
            <h2>Votre panier est vide</h2>
            <p>Decouvrez nos produits et ajoutez-les a votre panier</p>
            <Link to="/catalog" className="btn btn-primary">
              Decouvrir le catalogue
            </Link>
          </div>
        ) : (
          <div className="cart-content">
            {/* Cart Items */}
            <div className="cart-items-section">
              <div className="cart-items-header">
                <h2>Articles ({cart.itemCount})</h2>
                <button className="btn btn-secondary" onClick={() => setShowClearConfirm(true)}>
                  Vider le panier
                </button>
              </div>

              <div className="cart-items">
                {cart.items.map((item) => (
                  <div key={item.articleId} className="cart-item">
                    <div className="item-image">
                      {item.articleImage ? (
                        <img src={item.articleImage} alt={item.articleName} />
                      ) : (
                        <div className="no-image">📦</div>
                      )}
                    </div>

                    <div className="item-info">
                      <Link to={`/product/${item.articleId}`} className="item-name-link">
                        <h3 className="item-name">{item.articleName}</h3>
                      </Link>
                      <p className="item-reference">Ref: {item.articleReference}</p>
                      <p className="item-price">{formatPrice(item.unitPriceTTC)} / unite</p>
                      {item.stockAvailable !== undefined && item.stockAvailable > 0 && item.stockAvailable <= 10 && (
                        <p className="item-stock-warning">Plus que {item.stockAvailable} en stock</p>
                      )}
                    </div>

                    <div className="item-quantity">
                      <label>Quantite:</label>
                      <div className="quantity-controls">
                        <button
                          onClick={() => handleUpdateQuantity(item.articleId, item.quantity - 1)}
                          className="quantity-btn"
                          disabled={isLoading || item.quantity <= 1}
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => {
                            const newQty = parseInt(e.target.value) || 1;
                            // Limiter au stock disponible
                            const maxQty = item.stockAvailable !== undefined ? item.stockAvailable : 999;
                            handleUpdateQuantity(item.articleId, Math.min(newQty, maxQty));
                          }}
                          min="1"
                          max={item.stockAvailable || 999}
                          className="quantity-input"
                          disabled={isLoading}
                        />
                        <button
                          onClick={() => handleUpdateQuantity(item.articleId, item.quantity + 1)}
                          className="quantity-btn"
                          disabled={isLoading || (item.stockAvailable !== undefined && item.quantity >= item.stockAvailable)}
                          title={item.stockAvailable !== undefined && item.quantity >= item.stockAvailable ? `Stock limite a ${item.stockAvailable}` : ''}
                        >
                          +
                        </button>
                      </div>
                      {item.stockAvailable !== undefined && item.quantity >= item.stockAvailable && (
                        <span className="stock-limit-msg">Stock maximum atteint</span>
                      )}
                    </div>

                    <div className="item-total">
                      <span className="total-label">Total:</span>
                      <span className="total-value">{formatPrice(item.unitPriceTTC * item.quantity)}</span>
                    </div>

                    <button
                      className="btn-remove"
                      onClick={() => handleRemove(item.articleId)}
                      disabled={isLoading}
                      title="Retirer du panier"
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Cart Summary */}
            <div className="cart-summary">
              <h2>Recapitulatif</h2>

              <div className="summary-line">
                <span>Sous-total HT:</span>
                <span>{formatPrice(cart.totalHT)}</span>
              </div>

              <div className="summary-line">
                <span>TVA:</span>
                <span>{formatPrice(cart.totalTVA)}</span>
              </div>

              <div className="summary-line summary-total">
                <span>Total TTC:</span>
                <span>{formatPrice(cart.totalTTC)}</span>
              </div>

              <button className="btn btn-primary btn-checkout" onClick={handleCheckout}>
                Passer la commande
              </button>

              <Link to="/catalog" className="btn btn-secondary btn-continue">
                Continuer mes achats
              </Link>

              <div className="payment-info">
                <p className="info-title">Paiement securise</p>
                <p className="info-text">
                  Vos informations de paiement sont protegees et securisees
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modal Confirmation Vider Panier */}
      {showClearConfirm && (
        <div className="modal-overlay" onClick={() => setShowClearConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Vider le panier ?</h3>
            <p>Voulez-vous vraiment supprimer tous les articles de votre panier ?</p>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowClearConfirm(false)}>
                Annuler
              </button>
              <button className="btn btn-danger" onClick={handleClearCart}>
                Vider le panier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
